/**
 * Tab Synchronization Module - PROJ-035
 * Uses BroadcastChannel API for cross-tab token sync
 *
 * Prevents race conditions when multiple tabs refresh tokens simultaneously
 * by coordinating refresh attempts and broadcasting results across tabs.
 */

const CHANNEL_NAME = 'lineage-auth-sync';

export type TabSyncMessage =
  | { type: 'TOKEN_REFRESHED'; accessToken: string; refreshToken?: string; timestamp: number }
  | { type: 'SESSION_EXPIRED'; timestamp: number }
  | { type: 'USER_LOGGED_OUT'; timestamp: number }
  | { type: 'REFRESH_STARTED'; tabId: string; timestamp: number }
  | { type: 'REFRESH_COMPLETED'; tabId: string; success: boolean; timestamp: number };

type MessageListener = (message: TabSyncMessage) => void;

class TabSyncManager {
  private channel: BroadcastChannel | null = null;
  private tabId: string;
  private listeners: Map<string, Set<MessageListener>> = new Map();
  private isSupported: boolean = false;

  constructor() {
    this.tabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.init();
  }

  private init() {
    // Feature detection for BroadcastChannel
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel(CHANNEL_NAME);
        this.channel.onmessage = (event) => this.handleMessage(event.data);
        this.isSupported = true;
      } catch (error) {
        console.warn('[TabSync] BroadcastChannel not available:', error);
        this.isSupported = false;
      }
    } else {
      console.warn('[TabSync] BroadcastChannel API not supported in this browser');
      this.isSupported = false;
    }
  }

  private handleMessage(message: TabSyncMessage) {
    // Don't process messages from this tab
    if ('tabId' in message && message.tabId === this.tabId) {
      return;
    }

    // Notify listeners for this message type
    const listeners = this.listeners.get(message.type);
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener(message);
        } catch (error) {
          console.error('[TabSync] Error in message listener:', error);
        }
      });
    }

    // Notify listeners for 'all' events
    const allListeners = this.listeners.get('all');
    if (allListeners) {
      allListeners.forEach((listener) => {
        try {
          listener(message);
        } catch (error) {
          console.error('[TabSync] Error in all-events listener:', error);
        }
      });
    }
  }

  /**
   * Broadcast a message to all other tabs
   */
  broadcast(message: TabSyncMessage) {
    if (!this.isSupported || !this.channel) {
      // Silently fail if not supported
      return;
    }

    try {
      this.channel.postMessage(message);
    } catch (error) {
      console.error('[TabSync] Error broadcasting message:', error);
    }
  }

  /**
   * Listen for specific message types
   * @param type - Message type to listen for, or 'all' for all messages
   * @param callback - Function to call when message is received
   */
  on(type: string, callback: MessageListener) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(callback);
  }

  /**
   * Remove a message listener
   */
  off(type: string, callback: MessageListener) {
    const listeners = this.listeners.get(type);
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.listeners.delete(type);
      }
    }
  }

  /**
   * Get this tab's unique ID
   */
  getTabId(): string {
    return this.tabId;
  }

  /**
   * Check if BroadcastChannel is supported
   */
  supported(): boolean {
    return this.isSupported;
  }

  /**
   * Clean up resources
   */
  destroy() {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.listeners.clear();
  }
}

// Singleton instance
export const tabSync = new TabSyncManager();

// Export type for external use
export type { MessageListener };
